import { isNative } from "@/lib/capacitor";

/**
 * Native boot initialization. No-op on web.
 *
 * IMPORTANT: capacitor.config.ts sets launchAutoHide=false, so SplashScreen.hide()
 * here is the ONLY thing that hides the splash. We add a 3s safety-net timer
 * that hides the splash unconditionally, plus surface any plugin failures
 * via console.warn so they appear in Xcode/Safari Web Inspector.
 */
export async function initializeNative(): Promise<void> {
  if (!isNative) return;

  // Safety net: regardless of what else fails, hide the splash after 3s.
  setTimeout(() => {
    void import("@capacitor/splash-screen")
      .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 200 }))
      .catch(() => {});
  }, 3000);

  try {
    const [{ SplashScreen }, { StatusBar, Style }, { Keyboard, KeyboardResize }, { Purchases, LOG_LEVEL }] = await Promise.all([
      import("@capacitor/splash-screen"),
      import("@capacitor/status-bar"),
      import("@capacitor/keyboard"),
      import("@revenuecat/purchases-capacitor"),
    ]);

    // Initialize RevenueCat before any UI runs so Offerings + customer
    // info are warm by the time the Plans tab opens. The public iOS SDK
    // key is safe to ship in the bundle (RevenueCat distinguishes
    // public vs secret keys; this one is purpose-built for the client).
    // appUserID is set later via Purchases.logIn() when Supabase signs in.
    const rcKey = import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined;
    if (rcKey) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN })
        .catch((e) => console.warn("[nativeBoot] Purchases.setLogLevel failed", e));
      await Purchases.configure({ apiKey: rcKey })
        .catch((e) => console.warn("[nativeBoot] Purchases.configure failed", e));
    } else {
      console.warn("[nativeBoot] VITE_REVENUECAT_IOS_KEY missing — IAP disabled on this build");
    }

    await Keyboard.setResizeMode({ mode: KeyboardResize.Native })
      .catch((e) => console.warn("[nativeBoot] Keyboard.setResizeMode failed", e));
    // Push the WebView under the status bar so the active theme's paper-bg
    // pattern (Trapper Keeper stripes, Velvet Underground twilight, etc.)
    // paints all the way to the top of the screen instead of leaving a
    // flat band behind the time/signal/battery indicators. The status bar
    // text color is still controlled by setStyle below.
    await StatusBar.setOverlaysWebView({ overlay: true })
      .catch((e) => console.warn("[nativeBoot] StatusBar.setOverlaysWebView failed", e));
    await StatusBar.setStyle({ style: Style.Dark })
      .catch((e) => console.warn("[nativeBoot] StatusBar.setStyle failed", e));
    await SplashScreen.hide({ fadeOutDuration: 200 })
      .catch((e) => console.warn("[nativeBoot] SplashScreen.hide failed", e));
  } catch (err) {
    console.warn("[nativeBoot] plugin import failed; safety-net timer will hide splash", err);
  }
}
